import React, { useState } from 'react';
import { Gift, User, Gamepad2, Dumbbell, Car, Music, Camera, Briefcase, Settings, Zap } from 'lucide-react';

const ForHimPage = ({ setCurrentPage }) => {
  const [selectedInterest, setSelectedInterest] = useState('all');

  const maleInterests = [
    { id: 'technology', name: 'Teknologi', icon: Zap, color: 'blue' },
    { id: 'gaming', name: 'Gaming', icon: Gamepad2, color: 'purple' },
    { id: 'sports', name: 'Sport & Fitness', icon: Dumbbell, color: 'green' },
    { id: 'cars', name: 'Biler & Motor', icon: Car, color: 'red' },
    { id: 'music', name: 'Musikk', icon: Music, color: 'yellow' },
    { id: 'photography', name: 'Foto & Video', icon: Camera, color: 'indigo' },
    { id: 'work', name: 'Jobb & Karriere', icon: Briefcase, color: 'gray' },
    { id: 'diy', name: 'Håndverk & DIY', icon: Settings, color: 'orange' }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-accent-50 via-indigo-50 to-purple-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">👨</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Gaver til han</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Finn maskuline og gjennomtenkte gaver som treffer hans interesser og personlighet. 
              Fra teknologi til hobby - vi hjelper deg å velge noe han virkelig vil sette pris på.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-accent-500 to-indigo-600 text-white rounded-lg hover:from-accent-600 hover:to-indigo-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn gaver til han
            </button>
          </div>
        </div>
      </section>

      {/* Understanding Him Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Hva gjør en gave perfekt for han?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <User className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Praktisk nytte</h3>
                <p className="text-secondary-600">
                  Menn setter ofte pris på gaver de faktisk kan bruke i hverdagen. Tenk på hans rutiner, 
                  hobbyer og utfordringer - hva kan gjøre livet hans enklere eller mer interessant?
                </p>
              </div>

              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <Zap className="w-12 h-12 text-success-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Kvalitet over kvantitet</h3>
                <p className="text-secondary-600">
                  En høykvalitets gjenstand han kan bruke i årevis er ofte bedre enn mange små ting. 
                  Menn har ofte få, men gode ting de bruker regelmessig.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                <Gift className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Personlige interesser</h3>
                <p className="text-secondary-600">
                  Gaver som relaterer til hans spesifikke interesser og hobbyer viser at du kjenner ham godt 
                  og har lagt ned tanke i gavevalgetet.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interest Categories */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Utforsk gaver basert på hans interesser
            </h2>

            {/* Interest Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setSelectedInterest('all')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  selectedInterest === 'all'
                    ? 'bg-accent-500 text-white border-accent-500'
                    : 'bg-white text-secondary-700 border-secondary-300 hover:border-accent-300'
                }`}
              >
                Alle interesser
              </button>
              {maleInterests.map((interest) => (
                <button
                  key={interest.id}
                  onClick={() => setSelectedInterest(interest.id)}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${
                    selectedInterest === interest.id
                      ? `bg-${interest.color}-500 text-white border-${interest.color}-500`
                      : 'bg-white text-secondary-700 border-secondary-300 hover:border-secondary-400'
                  }`}
                >
                  <interest.icon className="w-4 h-4" />
                  <span>{interest.name}</span>
                </button>
              ))}
            </div>

            {/* Interest Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {maleInterests.map((interest) => (
                <div
                  key={interest.id}
                  className={`bg-white rounded-lg shadow-sm p-6 border border-secondary-100 hover:shadow-md transition-all ${
                    selectedInterest === 'all' || selectedInterest === interest.id ? 'block' : 'hidden'
                  }`}
                >
                  <interest.icon className={`w-12 h-12 text-${interest.color}-500 mb-4`} />
                  <h3 className="text-lg font-semibold text-secondary-800 mb-3">{interest.name}</h3>
                  <div className="space-y-2 text-sm text-secondary-600">
                    {interest.id === 'technology' && (
                      <>
                        <p>• Smart home-gadgets</p>
                        <p>• Trådløse ladeløsninger</p>
                        <p>• Høykvalitets hodetelefoner</p>
                        <p>• Smartklokker og fitness-trackere</p>
                      </>
                    )}
                    {interest.id === 'gaming' && (
                      <>
                        <p>• Gaming-tilbehør og utstyr</p>
                        <p>• Mekaniske tastaturer</p>
                        <p>• Gaming-stoler</p>
                        <p>• VR-utstyr og spill</p>
                      </>
                    )}
                    {interest.id === 'sports' && (
                      <>
                        <p>• Treningutstyr for hjemmet</p>
                        <p>• Sportsbeklædning</p>
                        <p>• Fitness-trackere</p>
                        <p>• Utendørs aktivitetsutstyr</p>
                      </>
                    )}
                    {interest.id === 'cars' && (
                      <>
                        <p>• Bilpleie og vaskeutstyr</p>
                        <p>• Bilgadgets og tilbehør</p>
                        <p>• Kjøreopplevelser</p>
                        <p>• Modelller og samlergjenstander</p>
                      </>
                    )}
                    {interest.id === 'music' && (
                      <>
                        <p>• Høykvalitets audio-utstyr</p>
                        <p>• Vinyl-plater og tilbehør</p>
                        <p>• Instrumenter og effekter</p>
                        <p>• Konsertbilletter</p>
                      </>
                    )}
                    {interest.id === 'photography' && (
                      <>
                        <p>• Kamerautstyr og objektiver</p>
                        <p>• Foto-redigeringsprogrammer</p>
                        <p>• Stativ og belysning</p>
                        <p>• Fotografikurs</p>
                      </>
                    )}
                    {interest.id === 'work' && (
                      <>
                        <p>• Kontororganisering</p>
                        <p>• Ergonomisk utstyr</p>
                        <p>• Profesjonell klær</p>
                        <p>• Produktivitetsverktøy</p>
                      </>
                    )}
                    {interest.id === 'diy' && (
                      <>
                        <p>• Kvalitetsverktøy</p>
                        <p>• Oppbevaringsløsninger</p>
                        <p>• Byggesett og prosjekter</p>
                        <p>• Verkstedsutstyr</p>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Age Considerations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Tenk på alder og livssituasjon
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">18-25 år</h3>
                <p className="text-secondary-600 text-sm mb-3">Student/ung voksen fase</p>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Gaming og teknologi</li>
                  <li>• Sportsutstyr</li>
                  <li>• Klær og stil</li>
                  <li>• Opplevelser med venner</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">26-35 år</h3>
                <p className="text-secondary-600 text-sm mb-3">Karriere og etablering</p>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Profesjonelle verktøy</li>
                  <li>• Hjem og interiør</li>
                  <li>• Kvalitetsklær</li>
                  <li>• Hobby-investering</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">36-50 år</h3>
                <p className="text-secondary-600 text-sm mb-3">Familie og velstand</p>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Luksusprodukter</li>
                  <li>• Familieopplevelser</li>
                  <li>• Helse og velvære</li>
                  <li>• Hjemmekontor-utstyr</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-error-50 rounded-lg p-6 border border-orange-100">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">50+ år</h3>
                <p className="text-secondary-600 text-sm mb-3">Modne interesser</p>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Samlergjenstander</li>
                  <li>• Reiseopplevelser</li>
                  <li>• Håndverkstradisjoner</li>
                  <li>• Tidløse kvalitetsting</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Relationship Context */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Din relasjon påvirker gavevalgetet
            </h2>

            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-accent-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">💑 Partner/Kjæreste</h3>
                <p className="text-secondary-600 mb-3">
                  Som partner kjenner du hans daglige rutiner og drømmer best. Gaver kan være personlige, 
                  romantiske eller relatert til felles opplevelser og fremtidsplaner.
                </p>
                <p className="text-sm text-secondary-500">
                  Tips: Tenk på noe han har nevnt i forbifarten, eller oppgrader noe han bruker daglig.
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-success-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">👨‍👩‍👧‍👦 Familie</h3>
                <p className="text-secondary-600 mb-3">
                  Som familiemedlem kan du gi mer personlige eller nostalgiske gaver. Tenk på hans 
                  barndom, familieminner eller støtte hans rolle som far/sønn/bror.
                </p>
                <p className="text-sm text-secondary-500">
                  Tips: Familierelaterte opplevelser eller noe som støtter hans interesser han ikke kjøper selv.
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">👥 Venn</h3>
                <p className="text-secondary-600 mb-3">
                  Som venn fokuser på delte interesser, humor eller noe som støtter hans hobbyer. 
                  Opplevelser dere kan dele sammen er ofte populært.
                </p>
                <p className="text-sm text-secondary-500">
                  Tips: Konsertbilletter, sports-relatert, eller noe knyttet til deres felles minner.
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-orange-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">🏢 Kollega</h3>
                <p className="text-secondary-600 mb-3">
                  Som kollega hold deg til profesjonelle eller nøytrale gaver. Kontorrelated ting, 
                  gavekort eller kvalitetsprodukter for hverdagsbruk er trygge valg.
                </p>
                <p className="text-sm text-secondary-500">
                  Tips: Unngå for personlige gaver. Fokuser på kvalitet og professjonalitet.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Unngå disse vanlige fallgruvene
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-error-50 border border-error-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-error-800 mb-4">❌ Ikke gjør dette</h3>
                <ul className="space-y-2 text-error-700">
                  <li>• Gi klær uten å vite størrelser og stil</li>
                  <li>• Anta interesser basert på kjønn</li>
                  <li>• Gi parfyme uten å kjenne hans preferanser</li>
                  <li>• Velge teknologi du ikke forstår</li>
                  <li>• Glemme aldersgruppe og livssituasjon</li>
                  <li>• Gi noe han allerede har mye av</li>
                </ul>
              </div>

              <div className="bg-success-50 border border-success-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-success-800 mb-4">✅ Gjør heller dette</h3>
                <ul className="space-y-2 text-success-700">
                  <li>• Spør diskret om ønsker og behov</li>
                  <li>• Observer hva han bruker og liker</li>
                  <li>• Velg kvalitet over kvantitet</li>
                  <li>• Tenk på hans spesifikke livsstil</li>
                  <li>• Gi opplevelser hvis du er usikker</li>
                  <li>• Inkluder en gjennomtenkt hilsen</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-accent-500 to-indigo-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å finne den perfekte gaven til han?
            </h2>
            <p className="text-xl text-accent-100 mb-8">
              Bruk vår gavefinner for å få personlige anbefalinger basert på hans interesser, alder og ditt budsjett
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-accent-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('recipient')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-accent-600 transition-colors font-semibold"
              >
                Se andre mottakere
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForHimPage;